<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRaffleApi } from '../services';

const props = defineProps<{
  id: string;
  sig: string;
}>();

const api = useRaffleApi();
const loading = ref(true);
const status = ref<'valid' | 'invalid' | 'error'>('valid'); // optimistica
const data = ref<any>(null);

onMounted(async () => {
  // Simulate verification delay for drama
  await new Promise(r => setTimeout(r, 1500));
  
  try {
      // In a real crypto implementation, we verify signature locally if public key is available
      // OR we ask backend to verify.
      // For now, let's fetch the sale and compare signatures.
      
      // We leverage the search endpoint.
      const res = await api.searchSales(props.id);
      const sale = res.data?.find((s: any) => s.id === props.id);
      
      if (sale && sale.qr_signature === props.sig && sale.approval_status === 'APPROVED') {
          status.value = 'valid';
          data.value = sale;
      } else {
          status.value = 'invalid';
      }
  } catch (e) {
      status.value = 'error';
  } finally {
      loading.value = false;
  }
});
</script>

<template>
  <div class="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center p-4 text-center">
    
    <!-- Loading State -->
    <div v-if="loading" class="animate-pulse flex flex-col items-center">
        <div class="w-24 h-24 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mb-8"></div>
        <h2 class="text-2xl font-bold text-white uppercase tracking-widest">Verificando Autenticidad...</h2>
        <p class="text-gray-400 mt-2">Conectando con la Blockchain de La Favorita</p>
    </div>

    <!-- VALID Result -->
    <div v-else-if="status === 'valid'" class="animate-in zoom-in duration-500 flex flex-col items-center max-w-md w-full bg-white rounded-3xl p-8 shadow-[0_0_100px_rgba(34,197,94,0.6)]">
        <div class="w-32 h-32 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-xl">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        
        <h1 class="text-4xl font-black text-green-600 mb-2 uppercase">Certificado Válido</h1>
        <p class="text-gray-500 text-sm mb-8 font-medium">Boleto Auténtico y Registrado</p>
        
        <div class="w-full space-y-4 bg-gray-50 results-card rounded-2xl p-6 border border-gray-100">
            <div class="flex justify-between border-b border-gray-200 pb-2">
                <span class="text-gray-400 font-bold text-xs uppercase">Referencia</span>
                <span class="font-mono font-bold text-gray-800">{{ data?.id?.substring(0,8) }}...</span>
            </div>
            <div class="flex justify-between border-b border-gray-200 pb-2">
                <span class="text-gray-400 font-bold text-xs uppercase">Titular</span>
                <span class="font-bold text-gray-800 text-right">{{ data?.full_name }}</span>
            </div>
            <div class="flex justify-between border-b border-gray-200 pb-2">
                <span class="text-gray-400 font-bold text-xs uppercase">Documento</span>
                <span class="font-bold text-gray-800">{{ data?.id_document }}</span>
            </div>
            <div class="pt-2">
                <span class="text-gray-400 font-bold text-xs uppercase block mb-1">Tickets</span>
                <p class="font-mono text-xl font-bold text-black break-all">{{ data?.tickets }}</p>
            </div>
        </div>
        
        <a href="/" class="mt-8 text-gray-400 text-xs font-bold uppercase tracking-widest hover:text-green-600 transition">Volver al Inicio</a>
    </div>

    <!-- INVALID Result -->
    <div v-else class="animate-in shake duration-500 flex flex-col items-center max-w-md w-full bg-red-900/10 border-2 border-red-500 rounded-3xl p-8 backdrop-blur-md">
        <div class="w-32 h-32 bg-red-600 rounded-full flex items-center justify-center mb-6 shadow-[0_0_50px_rgba(220,38,38,0.5)] animate-pulse">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </div>
        
        <h1 class="text-4xl font-black text-red-500 mb-4 uppercase">NO VÁLIDO</h1>
        <p class="text-white text-lg mb-8 text-center leading-relaxed">Este código QR no corresponde a un boleto activo o ha sido manipulado.</p>
        
        <div class="bg-red-500/20 p-4 rounded-xl border border-red-500/30 text-red-200 text-xs font-mono break-all">
            ID: {{ id }} <br/> SIG: {{ sig?.substring(0,16) }}...
        </div>
        
        <a href="/" class="mt-8 px-8 py-3 bg-white text-red-900 font-bold rounded-full hover:bg-gray-200 transition">Reportar Problema</a>
    </div>

  </div>
</template>

<style scoped>
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}
.shake {
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}
</style>
